<script setup lang="ts">
/**
 * 顶部工具条：历史（撤销/重做）· 绘图工具 · 对齐与分布。
 *
 * 版式：**悬浮卡片**（绝对定位在画布容器内，水平居中、距顶 12px），对齐 Excalidraw 的做法；
 * 它不占版式行，所以画布完整铺满中栏。左栏（图层）与右栏（属性）通高。
 */
import { useEditor } from '@/composables/useEditor';
import { setInspectorTab, ui, type Tool } from '@/stores/editor';

const editor = useEditor();

interface BarTool {
  id: string;
  label: string;
  d: string;
  disabled?: boolean;
}

const TOOLS: BarTool[] = [
  { id: 'select', label: '选择 V', d: 'M6 3.5 18.5 12l-5.9 1.4L9.8 19z' },
  {
    id: 'hand',
    label: '平移 H（空格拖拽）',
    d: 'M9 11V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6.5a1.5 1.5 0 0 1 3 0V14a6 6 0 0 1-6 6h-1a6 6 0 0 1-6-6v-2.5a1.5 1.5 0 0 1 3 0',
  },
  { id: 'text', label: '文本 T（点一下后直接输入）', d: 'M5 6h14M12 6v13M9 19h6' },

  // 可添加图形（直线 / 矩形 / 椭圆 / 折线 / 钢笔 / 路径节点 / 批注）：暂时隐藏，不给用户开放
  // （用户 2026-10-13 要求「把可添加图形先不向用户开放，文字 T 保留」）。
  // 要放出来就把下面这段取消注释，同时把 useHotkeys 里被注释的 L / R / E / P 键位一起放出来。
  // { id: 'line', label: '直线 L（拖拽）', d: 'M5 19 19 5' },
  // { id: 'rect', label: '矩形 R（拖拽）', d: 'M4 7h16v10H4z' },
  // { id: 'ellipse', label: '椭圆 E（拖拽）', d: 'M12 6c4.4 0 8 2.7 8 6s-3.6 6-8 6-8-2.7-8-6 3.6-6 8-6z' },
  // { id: 'polyline', label: '折线 P（多次点击，Enter/双击结束）', d: 'M4 17l5-7 4 4 7-9' },
  // { id: 'pen', label: '钢笔（v2）', d: 'M12 3l7 7-7 11-7-11z', disabled: true },
  // { id: 'path', label: '路径节点 A（v2）', d: 'M4 18c5 0 4-11 9-11 2.5 0 3.5 1.6 3.5 3', disabled: true },
  // {
  //   id: 'comment',
  //   label: '批注（v2）',
  //   d: 'M20 12a7 7 0 0 1-7 7H8l-4 3v-5.5A7 7 0 0 1 11 5h2a7 7 0 0 1 7 7z',
  //   disabled: true,
  // },
];

const pick = (id: string): void => {
  if (id === 'select' || id === 'hand') {
    ui.tool = id;
    return;
  }
  ui.tool = id as Tool;
};
</script>

<template>
  <nav class="topbar" aria-label="工具">
    <button class="rail-btn" :disabled="!editor.canUndo.value" title="撤销 Ctrl+Z" @click="editor.undo()">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8">
        <path d="M9 7H6a2 2 0 0 0-2 2v3" />
        <path d="M6 7 9 4" />
        <path d="M6 7l3 3" />
        <path d="M10 17h6a3 3 0 0 0 0-6H9" />
      </svg>
    </button>
    <button class="rail-btn" :disabled="!editor.canRedo.value" title="重做 Ctrl+Shift+Z" @click="editor.redo()">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8">
        <path d="M15 7h3a2 2 0 0 1 2 2v3" />
        <path d="m18 7-3-3" />
        <path d="m18 7-3 3" />
        <path d="M14 17H8a3 3 0 0 1 0-6h7" />
      </svg>
    </button>

    <span class="topbar-sep" />

    <button
      v-for="tool in TOOLS"
      :key="tool.id"
      class="rail-btn"
      :class="{ 'is-active': ui.tool === tool.id, 'is-disabled': tool.disabled }"
      :title="tool.label"
      :disabled="tool.disabled"
      @click="pick(tool.id)"
    >
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6">
        <path :d="tool.d" />
      </svg>
    </button>

    <span class="topbar-sep" />

    <button class="rail-btn" title="对齐与分布（右栏「排列」页签）" @click="setInspectorTab('arrange')">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6">
        <path d="M4 6v12M20 4v16M8 9h8v6H8z" />
      </svg>
    </button>
  </nav>
</template>
